// libs
import React, { useMemo } from 'react';
import cx from 'classnames';
import { useNavigate } from 'react-router-dom';
//widgets
import Tile from '@/widgets/Tile';
import ActionMenu from '../ActionMenu';
import BudgetProgressBar from '@/widgets/BudgetProgressBar';
import CampaignStatusLabels from '../CampaignStatus';
import MetricsTile from '@/widgets/MetricsTile/MetricsTile';
import GenerateReportButton from '@/widgets/GenerateReportButton';
import SummaryTable from '@/widgets/SummaryTable';
import EditButton from '@/widgets/EditButton/EditButton';
import EllipsizeContent from '@/widgets/EllipsizeContent/EllipsizeContent';
//stores
import useReadCampaignStore from '@/stores/ReadCampaign';
import useCampaignDetailsStore from '@/stores/CampaignDetails';
import useConfigStore from '@/stores/Configs';
import useAccountStore from '@/stores/account';
//helpers
import { checkEditableStatus, formatCampaignData as formatCampaignDataHelper, getCityNames, getDaysOfWeekLabels, getMealSlotsLabels, } from '@/helpers/campaign';
import { format, formatNumberIntl, prefixWithRupee } from '@/helpers/utils';
import { getParsedMetricValue } from '@/helpers/utils';
//dls
import { Text } from '@swiggy-private/dls-web-core/dist/components/Text';
//constants
import { DlsColorTokens, DlsFontVariants } from '@/constants/DLSTokens';
import Routes from '@/constants/Routes';
import { ROLES } from '@local/typedefs/dist/common/userRoles';
import { CampaignStatusMapEnum } from '@/constants/campaign';
import { CampaignDetailPerformanceSummaryTooltips } from '@/constants/tooltips';
// defs
import { CampaignStatus } from '@local/typedefs/dist/protos/campaign/Campaign';
//style
import style from './CampaignDetails.module.scss';
// components
import Tooltip from '@/components/Tooltip/Tooltip';
const SummaryTableRow = ({ label, value, plus, }) => {
    return (React.createElement(SummaryTable.Row, { widths: [40, 60] },
        React.createElement(SummaryTable.Cell, null,
            React.createElement(Text, { variant: DlsFontVariants.body_B2_Med, color: DlsColorTokens.text_Highest_Emphasis }, label)),
        React.createElement(SummaryTable.Cell, null,
            typeof value === 'string' ? (React.createElement(Text, { variant: DlsFontVariants.body_B2_Med, color: DlsColorTokens.text_Med_Emphasis }, value)) : (value),
            plus && (React.createElement(React.Fragment, null,
                "\u00A0",
                React.createElement(Text, { variant: DlsFontVariants.body_B2_Med, color: DlsColorTokens.primary }, plus))))));
};
const CampaignDetails = () => {
    const { campaignOpDisabled, campaignOpDisabledCallout } = useConfigStore((state) => ({
        campaignOpDisabled: state.campaignOpDisabled,
        campaignOpDisabledCallout: state.campaignOpDisabledCallout,
    }));
    const navigate = useNavigate();
    const { campaign } = useReadCampaignStore((state) => ({
        campaign: state.campaign,
    }));
    const { aggregateMetrics } = useCampaignDetailsStore((state) => ({
        aggregateMetrics: state.aggregateMetrics,
    }));
    const cityOptions = useConfigStore((state) => state.cities);
    const permissions = useAccountStore((state) => state.loggedInAccountDetails.permissions);
    const { haveCampaignEditPermission, haveReportDownloadPermission } = useMemo(() => ({
        haveCampaignEditPermission: permissions.includes(ROLES.EDIT_CAMPAIGN),
        haveReportDownloadPermission: permissions.includes(ROLES.DOWNLOAD_REPORT),
    }), [permissions]);
    const shouldShowGenerateReportButton = haveReportDownloadPermission &&
        (campaign === null || campaign === void 0 ? void 0 : campaign.status) !== CampaignStatus.CAMPAIGN_STATUS_BOOKED;
    const redirectToEdit = () => {
        navigate(format(Routes.INSTAMART.CAMPAIGN_EDIT, (campaign === null || campaign === void 0 ? void 0 : campaign.id) || ''), {
            preventScrollReset: true,
            state: {
                activeStep: 1,
            },
        });
    };
    const formatCampaignData = (key) => {
        return formatCampaignDataHelper(key, campaign, cityOptions);
    };
    const renderCampaignStatus = () => {
        const status = formatCampaignData('status');
        switch (status) {
            case CampaignStatusMapEnum.ACTIVE:
                return React.createElement(CampaignStatusLabels.Active, null);
            case CampaignStatusMapEnum.INACTIVE:
            case CampaignStatusMapEnum.INVALID:
            case CampaignStatusMapEnum.EXHAUSTED:
            case CampaignStatusMapEnum.EXPIRED:
            case CampaignStatusMapEnum.STOPPED:
            default:
                return React.createElement(CampaignStatusLabels.Inactive, { status: status });
        }
    };
    const renderCity = () => {
        const citiesName = getCityNames(campaign === null || campaign === void 0 ? void 0 : campaign.adGroups[0]['cityIds'], cityOptions);
        return React.createElement(EllipsizeContent, { options: citiesName });
    };
    const renderMealSlots = () => {
        const mealSlots = getMealSlotsLabels(campaign === null || campaign === void 0 ? void 0 : campaign.adGroups[0].mealSlots);
        return React.createElement(EllipsizeContent, { options: mealSlots });
    };
    const renderDaysOfWeek = () => {
        const daysOfWeek = getDaysOfWeekLabels(campaign === null || campaign === void 0 ? void 0 : campaign.adGroups[0].daysOfWeek);
        return React.createElement(EllipsizeContent, { options: daysOfWeek });
    };
    return (React.createElement(Tile, { className: cx(style.firstTile, style.tile) },
        React.createElement("div", { className: style.headerContent },
            React.createElement("div", { className: style.leftContent },
                React.createElement("div", { className: style.leftContentLineOne },
                    renderCampaignStatus(),
                    React.createElement(Text, { variant: DlsFontVariants.body_B2_Med, color: DlsColorTokens.text_Low_Emphasis, className: style.inline },
                        formatCampaignData('startDate'),
                        " -",
                        ' ',
                        formatCampaignData('endDate'))),
                React.createElement("div", { className: cx(style.leftContentLineTwo, {
                        [style.leftContentLineTwoWithoutAction]: !haveCampaignEditPermission,
                    }) },
                    React.createElement(Text, { variant: DlsFontVariants.header_H2_Black, color: DlsColorTokens.text_Highest_Emphasis }, formatCampaignData('name')),
                    haveCampaignEditPermission && (React.createElement(ActionMenu, { campaignName: formatCampaignData('name') || '', campaignId: (campaign === null || campaign === void 0 ? void 0 : campaign.id) || '', campaignStatus: formatCampaignData('status') })))),
            React.createElement("div", { className: style.rightContent },
                React.createElement(BudgetProgressBar, { max: ((campaign === null || campaign === void 0 ? void 0 : campaign.budget) || 0).toFixed(0), spent: ((aggregateMetrics === null || aggregateMetrics === void 0 ? void 0 : aggregateMetrics.budgetBurnt) || 0).toFixed(0) }))),
        React.createElement("hr", null),
        React.createElement("div", { className: style.metricsContent },
            React.createElement("div", { className: style.metricsTitle },
                React.createElement(Text, { variant: DlsFontVariants.header_H4_Black, color: DlsColorTokens.text_High_Emphasis }, "Campaign Metrics"),
                shouldShowGenerateReportButton && (React.createElement(GenerateReportButton, { onClick: () => void 0, label: "Generate Report", campaignName: formatCampaignData('name'), campaignIds: [(campaign === null || campaign === void 0 ? void 0 : campaign.id) || ''] }))),
            React.createElement("div", { className: style.metricsTilesContainer },
                React.createElement(MetricsTile, { label: "Total Impressions", metric: getParsedMetricValue(formatNumberIntl({
                        value: aggregateMetrics === null || aggregateMetrics === void 0 ? void 0 : aggregateMetrics.impressions,
                    })), tooltip: CampaignDetailPerformanceSummaryTooltips.TOTAL_IMPRESSIONS }),
                React.createElement(MetricsTile, { label: "Cart Additions", metric: getParsedMetricValue(formatNumberIntl({
                        value: aggregateMetrics === null || aggregateMetrics === void 0 ? void 0 : aggregateMetrics.addToCart,
                    })), tooltip: CampaignDetailPerformanceSummaryTooltips.CART_ADDITIONS }),
                React.createElement(MetricsTile, { label: "GMV", metric: prefixWithRupee(getParsedMetricValue(formatNumberIntl({
                        value: aggregateMetrics === null || aggregateMetrics === void 0 ? void 0 : aggregateMetrics.gmv,
                    }))), tooltip: CampaignDetailPerformanceSummaryTooltips.GMV }),
                React.createElement(MetricsTile, { label: "ROI", metric: getParsedMetricValue(formatNumberIntl({
                        value: aggregateMetrics === null || aggregateMetrics === void 0 ? void 0 : aggregateMetrics.roi,
                        maximumFractionDigits: 2,
                    })), tooltip: CampaignDetailPerformanceSummaryTooltips.ROI }))),
        React.createElement("div", { className: style.tableTitle },
            React.createElement(Text, { variant: DlsFontVariants.header_H5_Black, color: DlsColorTokens.text_Med_Emphasis, className: style.tableTitleText }, "Campaign details"),
            haveCampaignEditPermission &&
                checkEditableStatus(campaign === null || campaign === void 0 ? void 0 : campaign.status) &&
                (campaignOpDisabled ? (React.createElement(Tooltip, { inline: false, content: campaignOpDisabledCallout },
                    React.createElement(EditButton, { disabled: true, onClick: redirectToEdit, label: "Edit" }))) : (React.createElement(EditButton, { onClick: redirectToEdit, label: "Edit" })))),
        React.createElement("div", { className: style.tableContainer },
            React.createElement(SummaryTable.Table, { className: style.detailsTable },
                React.createElement(SummaryTableRow, { label: "Days", value: renderDaysOfWeek() }),
                React.createElement(SummaryTableRow, { label: "Meal Slots", value: renderMealSlots() }),
                React.createElement(SummaryTableRow, { label: "Format", value: "Keyword Ads on Swiggy Instamart" })),
            React.createElement(SummaryTable.Table, { className: style.detailsTable },
                React.createElement(SummaryTableRow, { label: "Locations", value: renderCity() }),
                React.createElement(SummaryTableRow, { label: "Pacing", value: formatCampaignData('pacingEnabled') || '' }),
                React.createElement(SummaryTableRow, { label: "Products and Keywords", value: formatCampaignData('products-keywords') || '-' })))));
};
export default CampaignDetails;
